import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faReceipt, faXmark, faTriangleExclamation, faMoneyBill, faCreditCard,
  faMobileScreen, faCircleCheck, faCheck, faBed, faWineBottle,
  faArrowRightArrowLeft, faCalendarPlus, faChevronDown, faChevronUp,
  faUpload, faIdCard, faUser,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./CheckInOutPage.css";
import MinibarModal from "../minibar/MinibarModal";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface Booking {
  id: string;
  bookingId: string;
  guestName: string;
  roomId: string;
  roomNumber?: string;
  roomType?: string;
  phone: string;
  checkIn: string | null;
  checkOut: string | null;
  nights: number;
  total: number;
  status?: string;
  bookingType?: string;
  roomReady?: boolean;
  alreadyPaid?: number;
}

interface BookingRoom {
  id: number;
  roomId: string;
  roomNumber: string;
  roomType: string;
  status: string;
  roomReady: boolean;
  guestName: string | null;
  adultsCount: number;
  childrenCount: number;
  extraBed: boolean;
  extraBedPrice: number;
  cot: boolean;
  cotPrice: number;
  roomPrice: number;
  totalPrice: number;
  paidPrice: number;
  paymentStatus: string;
}

interface PassportRecord {
  id: number;
  guest_index: number;
  roomid: string | null;
  guest_name: string | null;
  doc_type: string | null;
  passport_number: string | null;
  image_front: string | null;
  image_back: string | null;
}

type Tab = "Check In" | "Check Out" | "In House";
type PayMethod = "cash" | "visa" | "instapay";

interface PayForm {
  method: PayMethod;
  amount: number;
  cardNumber: string;
  transactionRef: string;
}

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function BookingTypeBadge({ type }: { type?: string }) {
  const map: Record<string, { color: string; bg: string; label: string }> = {
    Standard: { color: "#C9A84C", bg: "rgba(201,168,76,0.12)",  label: "Standard" },
    Booking:  { color: "#C9A84C", bg: "rgba(201,168,76,0.12)",  label: "Booking" },
    HouseUse: { color: "#34d399", bg: "rgba(52,211,153,0.12)",  label: "House Use" },
    Special:  { color: "#fbbf24", bg: "rgba(251,191,36,0.12)",  label: "Special" },
  };
  const m = map[type || "Standard"] || map.Standard;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: m.bg, color: m.color, letterSpacing: 0.4 }}>
      {m.label}
    </span>
  );
}

/* ── Receipt + Payment Modal ─────────────────────────────────────────────── */
function ReceiptModal({ booking, onClose, onPaid }: { booking: Booking; onClose: () => void; onPaid: (b: Booking) => void }) {
  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  const isNoCharge  = booking.bookingType === "HouseUse" || booking.bookingType === "Special";
  const isPrePaid   = booking.bookingType === "Booking";
  const alreadyPaid = booking.alreadyPaid ?? 0;
  const remaining   = Math.max(0, booking.total - alreadyPaid);
  const hasDeposit  = alreadyPaid > 0;

  const [pay, setPay] = useState<PayForm>({ method: "cash", amount: remaining, cardNumber: "", transactionRef: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmitPayment = async () => {
    if (!pay.amount || pay.amount <= 0) { setError("Amount required and must be > 0."); return; }
    if (pay.method === "visa" && !pay.cardNumber.trim()) { setError("Card number (last 4 digits) required."); return; }
    if (pay.method === "instapay" && !pay.transactionRef.trim()) { setError("Transaction reference required."); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API_BASE}/api/dashboard/payments/${booking.bookingId}/pay`, {
        amount: pay.amount, method: pay.method, note: "",
        card_number: pay.method === "visa" ? pay.cardNumber : null,
        transaction_ref: pay.method === "instapay" ? pay.transactionRef : null,
      }, { headers });
      await axios.put(`${API_BASE}/api/dashboard/checkout/${booking.id}`, {}, { headers });
      onPaid(booking);
    } catch (e: any) { setError(e?.response?.data?.message ?? e?.message ?? "Failed"); }
    finally { setSaving(false); }
  };

  const handleCheckoutNoCharge = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/dashboard/checkout/${booking.id}`, {}, { headers });
      onPaid(booking);
    } catch (e: any) { setError(e?.response?.data?.message ?? e?.message ?? "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="cio-modal-overlay" onClick={onClose}>
      <div className="cio-receipt-modal" onClick={e => e.stopPropagation()}>
        <div className="cio-receipt-header">
          <div className="cio-receipt-icon"><FontAwesomeIcon icon={faReceipt} /></div>
          <h2>{isNoCharge ? "No Charge — Checkout" : isPrePaid ? "Pre-paid — Checkout" : hasDeposit ? "Remaining Balance" : "Outstanding Payment"}</h2>
          <button className="cio-receipt-close" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
        <div className="cio-receipt-body">
          <div className="cio-receipt-card">
            {[["Guest", booking.guestName], ["Room", booking.roomNumber || booking.roomId], ["Check-in", fmt(booking.checkIn)], ["Check-out", fmt(booking.checkOut)], ["Nights", String(booking.nights)]].map(([l, v]) => (
              <div key={l} className="cio-receipt-row"><span className="cio-receipt-lbl">{l}</span><span className="cio-receipt-val">{v}</span></div>
            ))}
            <div className="cio-receipt-row"><span className="cio-receipt-lbl">Type</span><span className="cio-receipt-val"><BookingTypeBadge type={booking.bookingType} /></span></div>
            <div className="cio-receipt-divider" />
            <div className="cio-receipt-row cio-receipt-total">
              <span className="cio-receipt-lbl">Total</span>
              <span className="cio-receipt-val cio-receipt-total-val">{isNoCharge ? "No Charge" : `EGP ${booking.total.toLocaleString()}`}</span>
            </div>
            {hasDeposit && <>
              <div className="cio-receipt-row" style={{ color: "#34d399" }}>
                <span className="cio-receipt-lbl">Deposit Paid</span><span className="cio-receipt-val">− EGP {alreadyPaid.toLocaleString()}</span>
              </div>
              <div className="cio-receipt-row" style={{ fontWeight: 700, color: "#f59e0b" }}>
                <span className="cio-receipt-lbl">Remaining Due</span><span className="cio-receipt-val">EGP {remaining.toLocaleString()}</span>
              </div>
            </>}
          </div>
          {!isNoCharge && !isPrePaid && (
            <div className="cio-pay-form">
              <p className="cio-pay-form-title">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                {hasDeposit ? ` Deposit of EGP ${alreadyPaid.toLocaleString()} on record — collect remaining EGP ${remaining.toLocaleString()}` : " No payment on record — collect before checkout"}
              </p>
              <div className="cio-pay-method-row">
                {(["cash", "visa", "instapay"] as PayMethod[]).map(m => (
                  <button key={m} className={`cio-pay-method-btn ${pay.method === m ? "active" : ""}`} onClick={() => { setPay(p => ({ ...p, method: m })); setError(""); }}>
                    {m === "cash" ? <><FontAwesomeIcon icon={faMoneyBill} /> Cash</> : m === "visa" ? <><FontAwesomeIcon icon={faCreditCard} /> Visa</> : <><FontAwesomeIcon icon={faMobileScreen} /> Instapay</>}
                  </button>
                ))}
              </div>
              <div className="cio-pay-fields">
                <div className="cio-pay-field">
                  <label>Amount (EGP)</label>
                  <input type="number" min={0} value={pay.amount} onChange={e => setPay(p => ({ ...p, amount: Number(e.target.value) }))} />
                </div>
                {pay.method === "visa" && (
                  <div className="cio-pay-field">
                    <label>Card Number (last 4)</label>
                    <input type="text" maxLength={4} placeholder="e.g. 4242" value={pay.cardNumber} onChange={e => setPay(p => ({ ...p, cardNumber: e.target.value }))} />
                  </div>
                )}
                {pay.method === "instapay" && (
                  <div className="cio-pay-field">
                    <label>Transaction Reference</label>
                    <input type="text" placeholder="Transaction ID / ref number" value={pay.transactionRef} onChange={e => setPay(p => ({ ...p, transactionRef: e.target.value }))} />
                  </div>
                )}
              </div>
            </div>
          )}
          {isPrePaid && <div className="cio-prepaid-notice"><FontAwesomeIcon icon={faCircleCheck} /> This booking was fully paid at the time of booking.</div>}
          {error && <div className="cio-pay-error">{error}</div>}
        </div>
        <div className="cio-receipt-footer">
          <button className="cio-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          {isNoCharge || isPrePaid
            ? <button className="cio-btn-checkout" onClick={handleCheckoutNoCharge} disabled={saving}>{saving ? "Checking out…" : "Confirm Checkout"}</button>
            : <button className="cio-btn-checkout" onClick={handleSubmitPayment} disabled={saving || !pay.amount}>{saving ? "Processing…" : `Collect EGP ${Number(pay.amount || 0).toLocaleString()} & Check Out`}</button>
          }
        </div>
      </div>
    </div>
  );
}

/* ── Room Swap Modal ─────────────────────────────────────────────────────── */
function RoomSwapModal({ bookingRoomId, bookingId, currentRoom, onClose, onSwapped }: {
  bookingRoomId: number; bookingId: string; currentRoom: string; onClose: () => void; onSwapped: () => void;
}) {
  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };
  const [rooms, setRooms] = useState<any[]>([]);
  const [newRoomId, setNewRoomId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/api/rooms`, { headers })
      .then(r => setRooms((r.data || []).filter((rm: any) => rm.status !== "occupied")))
      .catch(() => {});
  }, []);

  const handleSwap = async () => {
    if (!newRoomId) { setError("Select a room"); return; }
    setSaving(true); setError("");
    try {
      await axios.put(`${API_BASE}/api/dashboard/room-swap/${bookingRoomId}`, { newRoomId, reason }, { headers });
      onSwapped();
    } catch (e: any) { setError(e?.response?.data?.error ?? "Swap failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="cio-modal-overlay" onClick={onClose}>
      <div className="swap-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="swap-modal-header">
          <div className="swap-modal-icon">
            <FontAwesomeIcon icon={faArrowRightArrowLeft} />
          </div>
          <div>
            <h3 className="swap-modal-title">Room Swap</h3>
            <p className="swap-modal-sub">Move guest to a different room</p>
          </div>
          <button className="swap-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Current room pill */}
        <div className="swap-current-room">
          <span className="swap-current-label">Current Room</span>
          <span className="swap-current-number">{currentRoom}</span>
        </div>

        {/* Arrow */}
        <div className="swap-arrow">
          <FontAwesomeIcon icon={faArrowRightArrowLeft} />
          <span>Move to</span>
        </div>

        {/* Fields */}
        <div className="swap-fields">
          <div className="swap-field">
            <label>Available Room</label>
            <select value={newRoomId} onChange={e => setNewRoomId(e.target.value)} className="swap-select">
              <option value="">Select a room…</option>
              {rooms.map(r => (
                <option key={r.roomid} value={r.roomid}>
                  Room {r.roomnumber} — {r.roomtype}
                </option>
              ))}
            </select>
          </div>
          <div className="swap-field">
            <label>Reason <span style={{ color: "rgba(242,235,217,0.3)", fontWeight: 400 }}>(optional)</span></label>
            <input
              type="text"
              className="swap-input"
              placeholder="e.g. guest request, maintenance issue…"
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="swap-error">
            <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
          </div>
        )}

        {/* Footer */}
        <div className="swap-footer">
          <button className="swap-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="swap-btn-confirm" onClick={handleSwap} disabled={saving || !newRoomId}>
            <FontAwesomeIcon icon={faArrowRightArrowLeft} />
            {saving ? "Moving…" : "Confirm Swap"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Extend Stay Modal ───────────────────────────────────────────────────── */
function ExtendStayModal({ bookingId, currentCheckout, onClose, onExtended }: {
  bookingId: string; currentCheckout: string; onClose: () => void; onExtended: (newOut: string) => void;
}) {
  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };
  const [newCheckout, setNewCheckout] = useState(currentCheckout);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleExtend = async () => {
    if (!newCheckout || newCheckout <= currentCheckout) { setError("New checkout must be after current checkout"); return; }
    setSaving(true); setError("");
    try {
      await axios.put(`${API_BASE}/api/dashboard/extend-stay/${bookingId}`, { newCheckout }, { headers });
      onExtended(newCheckout);
    } catch (e: any) { setError(e?.response?.data?.error ?? "Failed to extend stay"); }
    finally { setSaving(false); }
  };

  return (
    <div className="cio-modal-overlay" onClick={onClose}>
      <div className="extend-modal" onClick={e => e.stopPropagation()}>

        <div className="extend-modal-header">
          <div className="extend-modal-icon"><FontAwesomeIcon icon={faCalendarPlus} /></div>
          <div>
            <h3 className="extend-modal-title">Extend Stay</h3>
            <p className="extend-modal-sub">Move checkout to a later date</p>
          </div>
          <button className="extend-modal-close" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>

        <div className="extend-modal-body">
          <div className="extend-current-card">
            <span className="extend-current-label">Current Checkout</span>
            <span className="extend-current-val">{fmt(currentCheckout)}</span>
          </div>
          <div className="extend-field">
            <label>New Checkout Date</label>
            <input type="date" value={newCheckout} min={currentCheckout} onChange={e => setNewCheckout(e.target.value)} />
          </div>
          {error && <div className="extend-error">{error}</div>}
        </div>

        <div className="extend-modal-footer">
          <button className="extend-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="extend-btn-confirm" onClick={handleExtend} disabled={saving}>
            <FontAwesomeIcon icon={faCalendarPlus} />
            {saving ? "Extending…" : "Confirm Extension"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Inline Guest ID Upload ──────────────────────────────────────────────── */
function GuestIDUpload({ booking, room, guestIdx, guestName, onDone, headers }: {
  booking: Booking; room: BookingRoom; guestIdx: number; guestName: string;
  onDone: () => void; headers: Record<string, string>;
}) {
  const [docType, setDocType] = useState<"national_id" | "passport">("national_id");
  const [front, setFront] = useState<{ file: File | null; preview: string | null; path: string | null; uploading: boolean }>({ file: null, preview: null, path: null, uploading: false });
  const [back,  setBack]  = useState<{ file: File | null; preview: string | null; path: string | null; uploading: boolean }>({ file: null, preview: null, path: null, uploading: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const pickFile = (side: "front" | "back", f: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const s = { file: f, preview: e.target?.result as string, path: null, uploading: false };
      side === "front" ? setFront(s) : setBack(s);
    };
    reader.readAsDataURL(f);
  };

  const upload = async (side: "front" | "back") => {
    const state = side === "front" ? front : back;
    if (!state.file) return;
    const set = side === "front" ? setFront : setBack;
    set(s => ({ ...s, uploading: true })); setErr("");
    try {
      const form = new FormData();
      form.append("image", state.file);
      form.append("bookingId", booking.bookingId);
      form.append("guestIndex", String(guestIdx));
      form.append("side", side);
      form.append("guestName", guestName);
      const res = await axios.post(`${API_BASE}/api/passports/upload`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      set(s => ({ ...s, path: res.data.imagePath, uploading: false }));
    } catch (e: any) { setErr(e?.response?.data?.error || "Upload failed"); set(s => ({ ...s, uploading: false })); }
  };

  const canSave = docType === "passport" ? !!front.path : !!(front.path && back.path);

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setErr("");
    try {
      await axios.post(`${API_BASE}/api/passports`, {
        reservationno: booking.bookingId,
        guest_index: guestIdx,
        roomid: room.roomId,
        guest_name: guestName || null,
        doc_type: docType === "national_id" ? "National ID" : "Passport",
        image_front: front.path || null,
        image_back: back.path || null,
      }, { headers });
      onDone();
    } catch (e: any) { setErr(e?.response?.data?.error || "Save failed"); }
    finally { setSaving(false); }
  };

  const sideBox = (side: "front" | "back") => {
    const state = side === "front" ? front : back;
    const label = side === "front" ? (docType === "national_id" ? "Front" : "Passport") : "Back";
    return (
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{label}</div>
        <div
          onClick={() => document.getElementById(`ci-id-${booking.bookingId}-${guestIdx}-${side}`)?.click()}
          style={{
            border: `1.5px dashed ${state.path ? "#22c55e" : "#94a3b8"}`,
            borderRadius: 8,
            padding: "14px 10px",
            textAlign: "center",
            cursor: "pointer",
            background: state.path ? "rgba(34,197,94,0.05)" : "#f8fafc",
            minHeight: 90,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "border-color 0.15s, background 0.15s",
          }}
        >
          {state.preview
            ? <img src={state.preview} alt={side} style={{ maxHeight: 76, maxWidth: "100%", objectFit: "contain", borderRadius: 4 }} />
            : <span style={{ fontSize: 12, color: "#94a3b8", display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <FontAwesomeIcon icon={faUpload} style={{ fontSize: 18, color: "#64748b" }} />
                <span>Tap to choose</span>
              </span>
          }
          <input id={`ci-id-${booking.bookingId}-${guestIdx}-${side}`} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
            onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(side, f); }} />
        </div>
        {state.file && !state.path && (
          <button onClick={() => upload(side)} disabled={state.uploading}
            style={{ marginTop: 6, width: "100%", padding: "6px 0", borderRadius: 6, background: "#1d4ed8", border: "none", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", opacity: state.uploading ? 0.6 : 1 }}>
            {state.uploading ? "Uploading…" : "Upload"}
          </button>
        )}
        {state.path && <div style={{ marginTop: 5, fontSize: 10, color: "#22c55e", fontWeight: 700, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>✓ Uploaded</div>}
      </div>
    );
  };

  return (
    <div style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#1e40af", display: "flex", alignItems: "center", gap: 5 }}>
          <FontAwesomeIcon icon={faIdCard} /> Document Type
        </span>
        <select value={docType} onChange={e => { setDocType(e.target.value as any); setFront({ file: null, preview: null, path: null, uploading: false }); setBack({ file: null, preview: null, path: null, uploading: false }); }}
          style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#ffffff", color: "#1e293b", outline: "none", fontWeight: 600 }}>
          <option value="national_id">National ID (front + back)</option>
          <option value="passport">Passport</option>
        </select>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        {sideBox("front")}
        {docType === "national_id" && sideBox("back")}
      </div>
      {err && <div style={{ fontSize: 11, color: "#ef4444", marginBottom: 8, fontWeight: 600 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={save} disabled={saving || !canSave}
          style={{ padding: "6px 16px", borderRadius: 7, background: canSave ? "#1d4ed8" : "#e2e8f0", border: "none", color: canSave ? "#ffffff" : "#94a3b8", fontSize: 11, fontWeight: 700, cursor: !canSave ? "default" : "pointer", transition: "all 0.15s" }}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={onDone}
          style={{ padding: "6px 14px", borderRadius: 7, background: "#ffffff", border: "1px solid #e2e8f0", color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ── Per-Room Expand Panel ───────────────────────────────────────────────── */
function RoomExpandPanel({ booking, mode, onRoomAction, onSwap, canCheckIn, onPassportChange }: {
  booking: Booking;
  mode: "checkin" | "checkout";
  onRoomAction: (bookingId: string, roomId: string, roomBrId: number) => Promise<void>;
  onSwap: (brId: number, roomNum: string) => void;
  canCheckIn?: boolean;
  onPassportChange?: () => void;
}) {
  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };
  const [rooms, setRooms] = useState<BookingRoom[]>([]);
  const [passports, setPassports] = useState<PassportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Record<number, boolean>>({});
  const [uploadKey, setUploadKey] = useState<string | null>(null); // "roomId-guestIdx"

  const loadData = () => {
    Promise.all([
      axios.get(`${API_BASE}/api/dashboard/booking-rooms/${booking.bookingId}`, { headers }),
      axios.get(`${API_BASE}/api/passports/${booking.bookingId}`, { headers }),
    ])
      .then(([rr, pr]) => {
        setRooms(rr.data || []);
        setPassports(pr.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, [booking.bookingId]);

  if (loading) return <div style={{ padding: "12px 20px", fontSize: 12, color: "rgba(242,235,217,0.4)" }}>Loading rooms…</div>;

  const handleRoom = async (room: BookingRoom) => {
    setProcessing(p => ({ ...p, [room.id]: true }));
    try { await onRoomAction(booking.bookingId, room.roomId, room.id); }
    finally { setProcessing(p => ({ ...p, [room.id]: false })); }
  };

  // Get passports for a specific room + guest index
  const getGuestPassport = (roomId: string, guestIdx: number) =>
    passports.find(p => p.roomid === roomId && p.guest_index === guestIdx);

  const passportStatusLabel = (p: PassportRecord | undefined) => {
    if (!p) return { label: "Missing", color: "#f87171", bg: "rgba(248,113,113,0.08)" };
    if (p.image_front || p.image_back || p.passport_number)
      return { label: "On File", color: "#34d399", bg: "rgba(52,211,153,0.08)" };
    return { label: "Partial", color: "#f59e0b", bg: "rgba(245,158,11,0.08)" };
  };

  const allRooms = [...rooms].sort((a, b) => (a.roomNumber > b.roomNumber ? 1 : -1));

  return (
    <div style={{ background: "rgba(255,255,255,0.03)", borderTop: "1px solid rgba(201,168,76,0.12)", padding: "12px 20px 16px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {allRooms.map((room, roomIdx) => {
          const isDone = mode === "checkin" ? room.status === "checked-in" : room.status === "checked-out";
          const isActionable = mode === "checkin" ? (room.status === "confirmed" || room.status === "pending") : room.status === "checked-in";

          const guestSlots = (room.adultsCount || 1) + (room.childrenCount || 0);
          const mainGuestName = room.guestName || booking.guestName;

          return (
            <div key={room.id} style={{ background: "#0E1E38", border: "1px solid rgba(201,168,76,0.12)", borderRadius: 12, overflow: "hidden" }}>
              {/* Room header */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid rgba(201,168,76,0.07)", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 180 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#FDFAF4" }}>Room {room.roomNumber}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(242,235,217,0.4)", background: "rgba(201,168,76,0.07)", border: "1px solid rgba(201,168,76,0.12)", borderRadius: 5, padding: "1px 6px", fontFamily: "monospace" }}>{room.roomId}</span>
                  <span style={{ fontSize: 11, color: "rgba(242,235,217,0.4)" }}>{room.roomType}</span>
                  {room.extraBed && <span style={{ fontSize: 10, fontWeight: 700, color: "#f97316", background: "rgba(249,115,22,0.1)", padding: "1px 6px", borderRadius: 99 }}>Extra Bed</span>}
                  {room.cot && <span style={{ fontSize: 10, fontWeight: 700, color: "#8b5cf6", background: "rgba(139,92,246,0.1)", padding: "1px 6px", borderRadius: 99 }}>Cot</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {/* Ready badge */}
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
                    color: room.roomReady ? "#34d399" : "#f59e0b",
                    background: room.roomReady ? "rgba(52,211,153,0.1)" : "rgba(245,158,11,0.1)" }}>
                    <FontAwesomeIcon icon={faBed} /> {room.roomReady ? "Ready" : "Not Ready"}
                  </span>
                  {/* Print RC */}
                  <button onClick={() => window.open(`/rc/${booking.bookingId}`, "_blank")}
                    style={{ fontSize: 11, padding: "3px 10px", borderRadius: 7, background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.12)", color: "#C9A84C", cursor: "pointer" }}>
                    <FontAwesomeIcon icon={faReceipt} /> RC
                  </button>
                  {/* Swap (checkout) */}
                  {mode === "checkout" && (
                    <button onClick={() => onSwap(room.id, room.roomNumber)}
                      style={{ fontSize: 11, padding: "3px 10px", borderRadius: 7, background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)", color: "#6366f1", cursor: "pointer" }}>
                      <FontAwesomeIcon icon={faArrowRightArrowLeft} /> Swap
                    </button>
                  )}
                  {/* Check In / Out */}
                  {isDone ? (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#34d399" }}>
                      <FontAwesomeIcon icon={faCircleCheck} /> {mode === "checkin" ? "Checked In" : "Checked Out"}
                    </span>
                  ) : isActionable ? (
                    <button
                      className={`cio-action-btn ${mode === "checkin" ? "cio-btn-green" : "cio-btn-blue"}`}
                      style={{ fontSize: 11, padding: "4px 14px" }}
                      disabled={processing[room.id] || (mode === "checkin" && canCheckIn === false)}
                      title={mode === "checkin" && canCheckIn === false ? (!booking.roomReady ? "Room not ready" : "ID / passport missing") : ""}
                      onClick={() => handleRoom(room)}
                    >
                      {processing[room.id] ? "…" : mode === "checkin" ? "Check In" : "Check Out"}
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Guest list */}
              <div style={{ padding: "8px 14px 10px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(242,235,217,0.3)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                  <FontAwesomeIcon icon={faUser} /> Guests · {guestSlots} person{guestSlots !== 1 ? "s" : ""}
                </div>
                {Array.from({ length: guestSlots }).map((_, i) => {
                  const guestLabel = roomIdx === 0 && i === 0
                    ? `${mainGuestName} (Main)`
                    : i === 0 ? mainGuestName : `Guest ${i + 1}`;
                  const passport = getGuestPassport(room.roomId, i);
                  const { label: pLabel, color: pColor, bg: pBg } = passportStatusLabel(passport);
                  const key = `${room.roomId}-${i}`;
                  const isUploading = uploadKey === key;

                  return (
                    <div key={i}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderTop: i > 0 ? "1px solid rgba(201,168,76,0.07)" : undefined }}>
                        <span style={{ fontSize: 12, color: "#F2EBD9", flex: 1 }}>{guestLabel}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, color: pColor, background: pBg }}>
                          <FontAwesomeIcon icon={pLabel === "On File" ? faCircleCheck : faIdCard} /> {pLabel}
                        </span>
                        {pLabel !== "On File" && (
                          <button onClick={() => setUploadKey(isUploading ? null : key)}
                            style={{ fontSize: 11, padding: "3px 10px", borderRadius: 7,
                              background: isUploading ? "rgba(201,168,76,0.12)" : "rgba(37,99,235,0.08)",
                              border: `1px solid ${isUploading ? "#cbd5e1" : "rgba(37,99,235,0.25)"}`,
                              color: isUploading ? "#64748b" : "#2563eb", cursor: "pointer" }}>
                            <FontAwesomeIcon icon={faUpload} /> Upload ID
                          </button>
                        )}
                      </div>
                      {isUploading && (
                        <GuestIDUpload
                          booking={booking}
                          room={room}
                          guestIdx={i}
                          guestName={i === 0 ? mainGuestName : `Guest ${i + 1}`}
                          headers={headers}
                          onDone={() => {
                            setUploadKey(null);
                            loadData();
                            onPassportChange?.();
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function todayISO() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}-${String(n.getDate()).padStart(2,"0")}`;
}

/* ── Main Page ───────────────────────────────────────────────────────────── */
export default function CheckInOutPage() {
  const [activeTab, setActiveTab] = useState<Tab>("Check In");
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [arrivals, setArrivals]     = useState<Booking[]>([]);
  const [departures, setDepartures] = useState<Booking[]>([]);
  const [inHouse, setInHouse]       = useState<Booking[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [toast, setToast]           = useState("");
  const [processing, setProcessing] = useState<Record<string, boolean>>({});
  const [passportStatus, setPassportStatus] = useState<Record<string, "complete" | "partial" | "missing">>({});
  const [copiedId, setCopiedId]     = useState<string | null>(null);
  const [expanded, setExpanded]     = useState<Record<string, boolean>>({});

  const [receiptBooking, setReceiptBooking] = useState<Booking | null>(null);
  const [minibarBooking, setMinibarBooking] = useState<Booking | null>(null);
  const [balances, setBalances]             = useState<Record<string, { total: number; paid: number; remaining: number }>>({});
  const [generatingReceipt, setGeneratingReceipt] = useState<string | null>(null);
  const [receiptLink, setReceiptLink]       = useState<{ bookingId: string; url: string } | null>(null);
  const [roomSwap, setRoomSwap]             = useState<{ brId: number; roomNum: string; bookingId: string } | null>(null);
  const [extendStay, setExtendStay]         = useState<{ bookingId: string; checkout: string } | null>(null);

  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isToday = selectedDate === todayISO();

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const loadData = (date: string) => {
    setLoading(true); setError("");
    Promise.all([
      axios.get(`${API_BASE}/api/dashboard/notifications?date=${date}`, { headers }),
      axios.get(`${API_BASE}/api/dashboard/inhouse`, { headers }),
    ])
      .then(async ([notif, house]) => {
        const arr  = notif.data.todayArrivals   || [];
        const dep  = notif.data.todayDepartures || [];
        setArrivals(arr);
        setDepartures(dep);
        setInHouse(Array.isArray(house.data) ? house.data : []);

        if (dep.length > 0) {
          const ids = dep.map((b: any) => b.bookingId).filter(Boolean).join(",");
          try {
            const balRes = await axios.get(`${API_BASE}/api/dashboard/departure-balances?ids=${ids}`, { headers });
            setBalances(balRes.data || {});
          } catch { /* non-critical */ }
        }
        const allIds = arr.map((b: any) => b.bookingId).filter(Boolean);
        if (allIds.length > 0) {
          try {
            const statusRes = await axios.get(`${API_BASE}/api/passports/statuses?ids=${allIds.join(",")}`, { headers });
            setPassportStatus(statusRes.data || {});
          } catch { setPassportStatus({}); }
        }
        return allIds;
      })
      .catch(() => setError("Failed to load data"))
      .finally(() => setLoading(false));
  };

  const copyPreCheckinLink = (bookingId: string) => {
    const link = `${window.location.origin}/pre-checkin?booking=${encodeURIComponent(bookingId)}`;
    navigator.clipboard.writeText(link).catch(() => {});
    setCopiedId(bookingId); setTimeout(() => setCopiedId(null), 2000);
    showToast("Pre check-in link copied");
  };

  useEffect(() => { loadData(selectedDate); }, [selectedDate]);

  // Full check-in (all rooms at once)
  const handleCheckIn = async (booking: Booking) => {
    setProcessing(p => ({ ...p, [booking.id]: true }));
    try {
      await axios.put(`${API_BASE}/api/dashboard/checkin/${booking.id}`, {}, { headers });
      setArrivals(prev => prev.filter(b => b.id !== booking.id));
      setInHouse(prev => [{ ...booking, status: "checked-in" }, ...prev]);
      showToast(`${booking.guestName} checked in`);
    } catch { showToast("Check-in failed. Please try again."); }
    finally { setProcessing(p => ({ ...p, [booking.id]: false })); }
  };

  // Partial check-in for one room
  const handlePartialCheckIn = async (bookingId: string, roomId: string) => {
    try {
      await axios.put(`${API_BASE}/api/dashboard/checkin/${bookingId}`, { roomId }, { headers });
      showToast(`Room checked in`);
      // Refresh expanded panel by toggling
      setExpanded(p => ({ ...p, [bookingId]: false }));
      setTimeout(() => setExpanded(p => ({ ...p, [bookingId]: true })), 50);
    } catch { showToast("Partial check-in failed"); }
  };

  // Partial checkout for one room
  const handlePartialCheckOut = async (bookingId: string, roomId: string) => {
    try {
      await axios.put(`${API_BASE}/api/dashboard/checkout/${bookingId}`, { roomId }, { headers });
      showToast(`Room checked out`);
      setExpanded(p => ({ ...p, [bookingId]: false }));
      setTimeout(() => setExpanded(p => ({ ...p, [bookingId]: true })), 50);
    } catch { showToast("Partial checkout failed"); }
  };

  const handleCheckoutClick = async (booking: Booking) => {
    const bType = booking.bookingType || "Standard";
    const isNoCharge = bType === "HouseUse" || bType === "Special";
    const isPrePaid  = bType === "Booking";
    if (isNoCharge) { directCheckout(booking); return; }

    setProcessing(p => ({ ...p, [booking.id]: true }));
    try {
      const payRes = await axios.get(`${API_BASE}/api/dashboard/payments/${booking.bookingId}/records`, { headers });
      const records: any[] = payRes.data || [];
      const totalPaid = records.reduce((s, r) => s + Number(r.amount || 0), 0);
      const isFullyPaid = isPrePaid || totalPaid >= booking.total;
      if (isFullyPaid) { directCheckout(booking); }
      else { setReceiptBooking({ ...booking, alreadyPaid: totalPaid }); }
    } catch { setReceiptBooking(booking); }
    finally { setProcessing(p => ({ ...p, [booking.id]: false })); }
  };

  const directCheckout = async (booking: Booking) => {
    setProcessing(p => ({ ...p, [booking.id]: true }));
    try {
      await axios.put(`${API_BASE}/api/dashboard/checkout/${booking.id}`, {}, { headers });
      setDepartures(prev => prev.filter(b => b.id !== booking.id));
      setInHouse(prev => prev.filter(b => b.id !== booking.id));
      showToast(`${booking.guestName} checked out`);
    } catch { showToast("Check-out failed."); }
    finally { setProcessing(p => ({ ...p, [booking.id]: false })); }
  };

  const onReceiptPaid = (booking: Booking) => {
    setReceiptBooking(null);
    setDepartures(prev => prev.filter(b => b.id !== booking.id));
    setInHouse(prev => prev.filter(b => b.id !== booking.id));
    showToast(`${booking.guestName} checked out`);
  };

  const handlePrintRC = (bookingId: string) => window.open(`/rc/${bookingId}`, "_blank");

  const handleSendReceipt = async (bookingId: string) => {
    setGeneratingReceipt(bookingId);
    try {
      const res = await axios.post(`${API_BASE}/api/receipts/generate`, { bookingId }, { headers });
      const link = `${window.location.origin}/receipt/${res.data.token}`;
      setReceiptLink({ bookingId, url: link });
      navigator.clipboard.writeText(link).catch(() => {});
      showToast("Receipt link copied");
    } catch { showToast("Failed to generate receipt link"); }
    finally { setGeneratingReceipt(null); }
  };

  const toggleExpand = (id: string) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const tabs: { key: Tab; count: number }[] = [
    { key: "Check In",  count: arrivals.length },
    { key: "In House",  count: inHouse.length },
    { key: "Check Out", count: departures.length },
  ];

  return (
    <DashboardLayout>
      <div className="cio-page">
        <div className="cio-header">
          <div>
            <h1 className="cio-title">Check In / Check Out</h1>
            <p className="cio-subtitle">
              {isToday ? "Today's arrivals, in-house guests, and departures" : `Viewing ${new Date(selectedDate + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}`}
            </p>
          </div>
          <div className="cio-header-right">
            <div className="cio-header-stats">
              <div className="cio-stat-pill cio-pill-green"><span className="cio-pill-num">{arrivals.length}</span><span className="cio-pill-label">Arrivals</span></div>
              <div className="cio-stat-pill cio-pill-purple"><span className="cio-pill-num">{inHouse.length}</span><span className="cio-pill-label">In House</span></div>
              <div className="cio-stat-pill cio-pill-blue"><span className="cio-pill-num">{departures.length}</span><span className="cio-pill-label">Departures</span></div>
            </div>
          </div>
        </div>

        <div className="cio-tabs-row">
          <div className="cio-tabs">
            {tabs.map(({ key, count }) => (
              <button key={key} className={`cio-tab ${activeTab === key ? "active" : ""}`} onClick={() => setActiveTab(key)}>
                {key}<span className={`cio-tab-count ${activeTab === key ? "active" : ""}`}>{count}</span>
              </button>
            ))}
          </div>
          <div className="cio-date-picker-wrap">
            <input type="date" className="cio-date-input" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
            {!isToday && <button className="cio-today-btn" onClick={() => setSelectedDate(todayISO())}>Today</button>}
          </div>
        </div>

        {loading && <div className="cio-loading">Loading today's guests…</div>}
        {error && <div className="cio-error">{error}</div>}

        {!loading && !error && (
          <div className="cio-body">

            {/* ── CHECK IN ── */}
            {activeTab === "Check In" && (
              arrivals.length === 0
                ? <div className="cio-empty-state"><div className="cio-empty-icon"><FontAwesomeIcon icon={faCheck} /></div><p className="cio-empty-title">All checked in!</p><p className="cio-empty-sub">No more arrivals for today.</p></div>
                : <div className="cio-list">
                    {arrivals.map(b => (
                      <div key={b.id} className="cio-row-wrapper">
                        <div className="cio-row">
                          <div className="cio-avatar">{b.guestName?.[0] ?? "?"}</div>
                          <div className="cio-info">
                            <p className="cio-guest-name">{b.guestName}<span style={{ marginLeft: 8 }}><BookingTypeBadge type={b.bookingType} /></span></p>
                            <p className="cio-guest-detail">Room {b.roomNumber || b.roomId}{b.roomType ? ` · ${b.roomType}` : ""}{b.phone ? ` · ${b.phone}` : ""}</p>
                          </div>
                          <div className="cio-meta">
                            <span className="cio-meta-item"><span className="cio-meta-label">Check-in</span><span className="cio-meta-value">{fmt(b.checkIn)}</span></span>
                            <span className="cio-meta-item"><span className="cio-meta-label">Nights</span><span className="cio-meta-value">{b.nights}</span></span>
                            <span className="cio-meta-item"><span className="cio-meta-label">Total</span><span className="cio-meta-value">{b.bookingType === "HouseUse" || b.bookingType === "Special" ? <span style={{ color: "#34d399" }}>No charge</span> : `EGP ${b.total?.toLocaleString() ?? "—"}`}</span></span>
                            <span className="cio-meta-item"><span className="cio-meta-label">Room</span><span className="cio-meta-value">{b.roomReady ? <span style={{ color: "#34d399", fontWeight: 700 }}><FontAwesomeIcon icon={faCircleCheck} /> Ready</span> : <span style={{ color: "#f59e0b", fontWeight: 600 }}><FontAwesomeIcon icon={faBed} /> Not Ready</span>}</span></span>
                            <span className="cio-meta-item"><span className="cio-meta-label">ID / Passport</span><span className="cio-meta-value">{passportStatus[b.bookingId] === "complete" ? <span style={{ color: "#34d399", fontWeight: 700 }}><FontAwesomeIcon icon={faCircleCheck} /> On File</span> : passportStatus[b.bookingId] === "partial" ? <span style={{ color: "#6366f1", fontWeight: 600 }}><FontAwesomeIcon icon={faCircleCheck} /> Partial</span> : <span style={{ color: "#f87171", fontWeight: 600 }}>Missing</span>}</span></span>
                          </div>
                          {passportStatus[b.bookingId] !== "complete" && (
                            <button className="cio-action-btn" style={{ background: passportStatus[b.bookingId] === "partial" ? "rgba(99,102,241,0.1)" : "rgba(248,113,113,0.1)", border: passportStatus[b.bookingId] === "partial" ? "1px solid rgba(99,102,241,0.3)" : "1px solid rgba(248,113,113,0.3)", color: passportStatus[b.bookingId] === "partial" ? "#818cf8" : "#f87171", fontSize: 11 }} onClick={() => copyPreCheckinLink(b.bookingId)} title="Copy pre check-in link">
                              {copiedId === b.bookingId ? "Copied!" : "Copy ID Link"}
                            </button>
                          )}
                          <button className="cio-action-btn" style={{ background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.12)", color: "#C9A84C", fontSize: 11 }} onClick={() => handlePrintRC(b.bookingId)} title="Print Registration Card">Print RC</button>
                          <button className="cio-action-btn" style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", color: "#6366f1", fontSize: 11 }} onClick={() => toggleExpand(b.id)} title="Expand rooms">
                            <FontAwesomeIcon icon={expanded[b.id] ? faChevronUp : faChevronDown} /> Rooms
                          </button>
                          <button
                            className="cio-action-btn cio-btn-green"
                            disabled={processing[b.id] || !b.roomReady || passportStatus[b.bookingId] !== "complete"}
                            title={!b.roomReady ? "Room is not ready yet" : passportStatus[b.bookingId] !== "complete" ? "Guest ID / passport is missing" : ""}
                            onClick={() => handleCheckIn(b)}
                          >
                            {processing[b.id] ? "Processing…" : "Check In All"}
                          </button>
                        </div>
                        {expanded[b.id] && (
                          <RoomExpandPanel
                            booking={b} mode="checkin"
                            canCheckIn={b.roomReady && passportStatus[b.bookingId] === "complete"}
                            onRoomAction={(bid, rid) => handlePartialCheckIn(bid, rid)}
                            onSwap={(brId, roomNum) => setRoomSwap({ brId, roomNum, bookingId: b.bookingId })}
                            onPassportChange={() => {
                              const ids = arrivals.map((a: any) => a.bookingId).filter(Boolean);
                              if (!ids.length) return;
                              axios.get(`${API_BASE}/api/passports/statuses?ids=${ids.join(",")}`, { headers })
                                .then(r => setPassportStatus(r.data || {}))
                                .catch(() => {});
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
            )}

            {/* ── IN HOUSE ── */}
            {activeTab === "In House" && (
              inHouse.length === 0
                ? <div className="cio-empty-state"><div className="cio-empty-icon cio-icon-purple">⌂</div><p className="cio-empty-title">No guests in house</p><p className="cio-empty-sub">No one is currently checked in.</p></div>
                : <div className="cio-list">
                    {inHouse.map(b => (
                      <div key={b.id} className="cio-row-wrapper">
                        <div className="cio-row">
                          <div className="cio-avatar cio-avatar-purple">{b.guestName?.[0] ?? "?"}</div>
                          <div className="cio-info">
                            <p className="cio-guest-name">{b.guestName}<span style={{ marginLeft: 8 }}><BookingTypeBadge type={b.bookingType} /></span></p>
                            <p className="cio-guest-detail">Room {b.roomNumber || b.roomId}{b.roomType ? ` · ${b.roomType}` : ""}{b.phone ? ` · ${b.phone}` : ""}</p>
                          </div>
                          <div className="cio-meta">
                            <span className="cio-meta-item"><span className="cio-meta-label">Checked In</span><span className="cio-meta-value">{fmt(b.checkIn)}</span></span>
                            <span className="cio-meta-item"><span className="cio-meta-label">Check Out</span><span className="cio-meta-value">{fmt(b.checkOut)}</span></span>
                            <span className="cio-meta-item"><span className="cio-meta-label">Total</span><span className="cio-meta-value">{b.bookingType === "HouseUse" || b.bookingType === "Special" ? <span style={{ color: "#34d399" }}>No charge</span> : b.total != null ? `EGP ${b.total.toLocaleString()}` : "—"}</span></span>
                          </div>
                          <button className="cio-action-btn" style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", color: "#6366f1", fontSize: 11 }} onClick={() => toggleExpand(b.id)} title="View rooms">
                            <FontAwesomeIcon icon={expanded[b.id] ? faChevronUp : faChevronDown} /> Rooms
                          </button>
                          <button className="cio-action-btn" style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", color: "#6366f1", fontSize: 11 }} onClick={() => setExtendStay({ bookingId: b.bookingId, checkout: b.checkOut || todayISO() })} title="Extend stay">
                            <FontAwesomeIcon icon={faCalendarPlus} /> Extend
                          </button>
                          <button className="cio-action-btn" style={{ background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.3)", color: "#34d399", fontSize: 11 }} onClick={() => handleSendReceipt(b.bookingId)} disabled={generatingReceipt === b.bookingId} title="Generate receipt link">
                            {generatingReceipt === b.bookingId ? "…" : "Receipt Link"}
                          </button>
                          <button className="cio-action-btn cio-btn-minibar" onClick={() => setMinibarBooking(b)} title="Add minibar charges"><FontAwesomeIcon icon={faWineBottle} /></button>
                          <button className="cio-action-btn cio-btn-blue" disabled={processing[b.id]} onClick={() => handleCheckoutClick(b)}>
                            {processing[b.id] ? "Checking…" : "Check Out All"}
                          </button>
                        </div>
                        {expanded[b.id] && (
                          <RoomExpandPanel
                            booking={b} mode="checkout"
                            onRoomAction={(bid, rid) => handlePartialCheckOut(bid, rid)}
                            onSwap={(brId, roomNum) => setRoomSwap({ brId, roomNum, bookingId: b.bookingId })}
                          />
                        )}
                      </div>
                    ))}
                  </div>
            )}

            {/* ── CHECK OUT ── */}
            {activeTab === "Check Out" && (
              departures.length === 0
                ? <div className="cio-empty-state"><div className="cio-empty-icon cio-icon-blue"><FontAwesomeIcon icon={faCheck} /></div><p className="cio-empty-title">All checked out!</p><p className="cio-empty-sub">No more departures for today.</p></div>
                : <div className="cio-list">
                    {departures.map(b => {
                      const bal = balances[b.bookingId];
                      const isNoCharge = b.bookingType === "HouseUse" || b.bookingType === "Special";
                      const remaining = bal?.remaining ?? 0;
                      const hasBalance = !isNoCharge && remaining > 0;
                      return (
                        <div key={b.id} className="cio-row-wrapper">
                          <div className={`cio-row${hasBalance ? " cio-row-warn" : ""}`}>
                            <div className="cio-avatar cio-avatar-blue">{b.guestName?.[0] ?? "?"}</div>
                            <div className="cio-info">
                              <p className="cio-guest-name">{b.guestName}<span style={{ marginLeft: 8 }}><BookingTypeBadge type={b.bookingType} /></span>
                                {hasBalance && <span className="cio-balance-warn"><FontAwesomeIcon icon={faTriangleExclamation} />EGP {remaining.toLocaleString()} due</span>}
                              </p>
                              <p className="cio-guest-detail">Room {b.roomNumber || b.roomId}{b.roomType ? ` · ${b.roomType}` : ""}</p>
                            </div>
                            <div className="cio-meta">
                              <span className="cio-meta-item"><span className="cio-meta-label">Nights Stayed</span><span className="cio-meta-value">{b.nights}</span></span>
                              <span className="cio-meta-item"><span className="cio-meta-label">Total</span><span className="cio-meta-value">{isNoCharge ? <span style={{ color: "#34d399" }}>No charge</span> : b.total != null ? `EGP ${b.total.toLocaleString()}` : "—"}</span></span>
                            </div>
                            <button className="cio-action-btn" style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", color: "#6366f1", fontSize: 11 }} onClick={() => toggleExpand(b.id)} title="Expand rooms">
                              <FontAwesomeIcon icon={expanded[b.id] ? faChevronUp : faChevronDown} /> Rooms
                            </button>
                            <button className="cio-action-btn" style={{ background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.3)", color: "#34d399", fontSize: 11 }} onClick={() => handleSendReceipt(b.bookingId)} disabled={generatingReceipt === b.bookingId}>
                              {generatingReceipt === b.bookingId ? "…" : "Receipt Link"}
                            </button>
                            <button className="cio-action-btn cio-btn-minibar" onClick={() => setMinibarBooking(b)} title="Add minibar charges"><FontAwesomeIcon icon={faWineBottle} /></button>
                            <button className="cio-action-btn cio-btn-blue" disabled={processing[b.id]} onClick={() => handleCheckoutClick(b)}>
                              {processing[b.id] ? "Checking…" : "Check Out All"}
                            </button>
                          </div>
                          {expanded[b.id] && (
                            <RoomExpandPanel
                              booking={b} mode="checkout"
                              onRoomAction={(bid, rid) => handlePartialCheckOut(bid, rid)}
                              onSwap={(brId, roomNum) => setRoomSwap({ brId, roomNum, bookingId: b.bookingId })}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
            )}
          </div>
        )}

        {toast && <div className="cio-toast">{toast}</div>}
      </div>

      {receiptBooking && <ReceiptModal booking={receiptBooking} onClose={() => setReceiptBooking(null)} onPaid={onReceiptPaid} />}

      {minibarBooking && (
        <MinibarModal
          bookingId={minibarBooking.bookingId} guestName={minibarBooking.guestName}
          roomNumber={minibarBooking.roomNumber || minibarBooking.roomId}
          onClose={() => setMinibarBooking(null)}
          onCharged={({ newTotal }) => {
            const update = (list: Booking[]) => list.map(b => b.bookingId === minibarBooking.bookingId ? { ...b, total: newTotal } : b);
            setInHouse(update); setDepartures(update);
          }}
        />
      )}

      {roomSwap && (
        <RoomSwapModal
          bookingRoomId={roomSwap.brId} bookingId={roomSwap.bookingId} currentRoom={roomSwap.roomNum}
          onClose={() => setRoomSwap(null)}
          onSwapped={() => {
            setRoomSwap(null);
            loadData(selectedDate);
            showToast("Room swapped successfully");
          }}
        />
      )}

      {extendStay && (
        <ExtendStayModal
          bookingId={extendStay.bookingId} currentCheckout={extendStay.checkout}
          onClose={() => setExtendStay(null)}
          onExtended={(newOut) => {
            setExtendStay(null);
            setInHouse(prev => prev.map(b => b.bookingId === extendStay.bookingId ? { ...b, checkOut: newOut } : b));
            showToast("Stay extended successfully");
          }}
        />
      )}
    </DashboardLayout>
  );
}
